import { buildLineChartSeries } from '@/features/executions/utils/chartData';
import {
  buildDateRangeFilters,
  buildEqFilter,
  buildInFilter,
  buildInFilterWithNull,
} from '@/ui/composites/grid/gridFilters';
import type { EntityStatusKey, ExecutionStatusKey } from './useEntityTrendsPage.types';
import type { LineChartDatum } from '@/features/executions/utils/chartData';
import type { MetricsSeriesPoint } from '@/features/metrics/metricsHelpers';
import type { ApiFilter, GridQueryInput } from '@/ui/composites/grid/gridUtils';

export const buildSelectedDayFilters = (
  selectedStartDay: string | null,
  selectedEndDay: string | null,
): GridQueryInput['filters'] => {
  if (!selectedStartDay && !selectedEndDay) return [];
  const start = selectedStartDay ? `${selectedStartDay}T00:00:00.000Z` : null;
  const end = selectedEndDay ? `${selectedEndDay}T23:59:59.999Z` : null;
  return buildDateRangeFilters('timestamp', start, end);
};

export const getExecutionStatusSelection = (
  selectedStatuses: EntityStatusKey[],
): ExecutionStatusKey[] =>
  selectedStatuses.filter((status): status is ExecutionStatusKey => status !== 'untested');

export const buildEntityStatusFilters = (
  selectedStatuses: EntityStatusKey[],
): GridQueryInput['filters'] => {
  if (selectedStatuses.length === 0) return [];
  const hasUntested = selectedStatuses.includes('untested');
  const statusValues = getExecutionStatusSelection(selectedStatuses);
  const filterValues: Array<string | null> = hasUntested ? [null, ...statusValues] : statusValues;
  return buildInFilterWithNull('latestRunStatus', filterValues);
};

export const buildExecutionStatusFilters = (
  selectedStatuses: EntityStatusKey[],
): GridQueryInput['filters'] => {
  if (selectedStatuses.length === 0) return [];
  const statusValues = getExecutionStatusSelection(selectedStatuses);
  if (statusValues.length === 0) {
    return buildEqFilter('id', 'no-match');
  }
  return buildInFilter('status', statusValues);
};

const translateRelatedEntityFilterField = (
  field: string,
  entitySearchField: string,
  executionSearchField: string,
): string => {
  if (field === executionSearchField) {
    return entitySearchField;
  }
  if (field === 'timestamp') {
    return 'latestRunTimestamp';
  }
  if (field === 'status') {
    return 'latestRunStatus';
  }
  return field;
};

export const buildRelatedEntitySummaryFilters = ({
  isLatestMode,
  entityFilters,
  executionFilters,
  entitySearchField,
  executionSearchField,
}: {
  isLatestMode: boolean;
  entityFilters: GridQueryInput['filters'];
  executionFilters: GridQueryInput['filters'];
  entitySearchField: string;
  executionSearchField: string;
}): GridQueryInput['filters'] => {
  if (isLatestMode) {
    return entityFilters;
  }

  return executionFilters.map((filter: ApiFilter) => ({
    ...filter,
    field: translateRelatedEntityFilterField(filter.field, entitySearchField, executionSearchField),
  }));
};

export const buildChartFilterIndices = (
  chartDates: string[],
  chartFilterRange: { start: string; end: string } | null,
): number[] | null => {
  if (!chartFilterRange) return null;
  const { start, end } = chartFilterRange;
  return chartDates
    .map((date, index) => (date >= start && date <= end ? index : -1))
    .filter((index) => index >= 0);
};

export const filterSeriesByIndices = <T>(
  series: T[] | null,
  indices: number[] | null,
): T[] | null => {
  if (!series || !indices) return series;
  return indices.map((index) => series[index]).filter((item): item is T => Boolean(item));
};

export const buildLineChartDataOrFallback = ({
  series,
  labels,
  dates,
  key,
  isFetched,
  hasError = false,
}: {
  series: MetricsSeriesPoint[] | undefined;
  labels: string[];
  dates: string[];
  key: string;
  isFetched: boolean;
  hasError?: boolean;
}): LineChartDatum[] | null => {
  const resolvedSeries = buildLineChartSeries(series, labels, dates, key);
  if (resolvedSeries) return resolvedSeries;
  return isFetched && !hasError ? [] : null;
};

export const filterChartRowsByIndices = <TRow extends object>(
  chartRows: TRow[] | null,
  indices: number[] | null,
): TRow[] | null => {
  if (!chartRows || !indices) return chartRows;
  return indices.map((index) => chartRows[index]).filter((row): row is TRow => Boolean(row));
};

export const resolveChartDateBounds = (
  chartDates: string[],
): { startDay: string | null; endDay: string | null } => ({
  startDay: chartDates[0] ?? null,
  endDay: chartDates.length > 0 ? (chartDates[chartDates.length - 1] ?? null) : null,
});

export const shouldShowSingleDayChartPoints = ({
  isLatestMode,
  chartSelectedStartDay,
  chartSelectedEndDay,
  selectedStartDay,
  selectedEndDay,
}: {
  isLatestMode: boolean;
  chartSelectedStartDay: string | null;
  chartSelectedEndDay: string | null;
  selectedStartDay: string | null;
  selectedEndDay: string | null;
}): boolean => {
  const start = isLatestMode ? chartSelectedStartDay : selectedStartDay;
  const end = isLatestMode ? (chartSelectedEndDay ?? chartSelectedStartDay) : selectedEndDay;
  return Boolean(start && end && start === end);
};
